import React, { useState, useRef, useEffect } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Box, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import { GripVertical, EyeOff } from "lucide-react";
import { WIDGET_REGISTRY } from "./widgetRegistry";

export const SortableWidgetWrapper = ({
  widgetConfig,
  isCustomizing,
  onColSpanChange,
  onHeightChange,
  onHide,
  children,
}) => {
  const { id, colSpan, rowHeight = 240, hidden } = widgetConfig;
  const registryInfo = WIDGET_REGISTRY[id] || {};

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !isCustomizing });

  const cardRef = useRef(null);

  // Live drag state for resizing
  const [isResizing, setIsResizing] = useState(false);
  const [liveColSpan, setLiveColSpan] = useState(colSpan);
  const [liveRowHeight, setLiveRowHeight] = useState(rowHeight);

  // Keep live values synced when prop changes and not resizing
  useEffect(() => {
    if (!isResizing) {
      setLiveColSpan(colSpan);
      setLiveRowHeight(rowHeight);
    }
  }, [colSpan, rowHeight, isResizing]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isResizing ? "none" : transition,
    opacity: isDragging ? 0.4 : hidden ? 0.5 : 1,
  };

  // Mouse Drag Handler for Bottom-Right Corner Handle
  const handleResizeMouseDown = (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (!cardRef.current) return;

    setIsResizing(true);
    const startX = e.clientX;
    const startY = e.clientY;
    const startRect = cardRef.current.getBoundingClientRect();
    const parentWidth = cardRef.current.parentElement?.getBoundingClientRect().width || startRect.width * (12 / colSpan);
    const singleColWidth = parentWidth / 12;

    const minSpan = registryInfo.minColSpan || 2;
    const maxSpan = registryInfo.maxColSpan || 12;

    let currentCalculatedSpan = liveColSpan;
    let currentCalculatedHeight = liveRowHeight;

    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;

      // Calculate candidate colSpan
      const rawSpan = Math.round((startRect.width + deltaX) / singleColWidth);
      const newSpan = Math.max(minSpan, Math.min(maxSpan, rawSpan));

      // Calculate candidate rowHeight (snapping to 20px steps)
      const rawHeight = startRect.height + deltaY - 40;
      const newHeight = Math.max(180, Math.min(650, Math.round(rawHeight / 20) * 20));

      currentCalculatedSpan = newSpan;
      currentCalculatedHeight = newHeight;

      setLiveColSpan(newSpan);
      setLiveRowHeight(newHeight);
    };

    const handleMouseUp = () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      setIsResizing(false);

      if (onColSpanChange) onColSpanChange(id, currentCalculatedSpan);
      if (onHeightChange) onHeightChange(id, currentCalculatedHeight);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  if (hidden && !isCustomizing) {
    return null;
  }

  const activeSpan = isResizing ? liveColSpan : colSpan;
  const activeHeight = isResizing ? liveRowHeight : rowHeight;

  return (
    <Grid
      item
      xs={12}
      sm={activeSpan > 6 ? 12 : activeSpan > 3 ? 6 : 6}
      md={activeSpan > 6 ? 12 : activeSpan}
      lg={activeSpan}
      ref={setNodeRef}
      style={style}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
      }}
    >
      <Box
        ref={cardRef}
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          borderRadius: "16px",
          border: isCustomizing ? "2px solid #3B82F6" : "none",
          backgroundColor: isCustomizing ? "#FFFFFF" : "transparent",
          boxShadow: isCustomizing ? "0px 8px 24px rgba(59, 130, 246, 0.15)" : "none",
          transition: isResizing ? "none" : "all 0.2s ease-in-out",
          p: isCustomizing ? 0.75 : 0,
          boxSizing: "border-box",
        }}
      >
        {/* Top Header Banner in Customize Mode */}
        {isCustomizing && (
          <Box
            sx={{
              backgroundColor: "#2563EB",
              color: "#FFFFFF",
              borderRadius: "10px 10px 8px 8px",
              px: 1.5,
              py: 0.65,
              mb: 0.75,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              boxSizing: "border-box",
              boxShadow: "0px 2px 6px rgba(37, 99, 235, 0.2)",
              flexShrink: 0,
            }}
          >
            {/* Drag Handle & Label */}
            <Box
              {...attributes}
              {...listeners}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                flex: 1,
                minWidth: 0,
                cursor: "grab",
                "&:active": { cursor: "grabbing" },
              }}
            >
              <GripVertical size={16} color="#FFFFFF" style={{ flexShrink: 0 }} />
              <Typography
                variant="caption"
                fontWeight="700"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: "0.775rem",
                  letterSpacing: "0.02em",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                :: Drag To Move ({registryInfo.title || id})
              </Typography>
            </Box>

            {/* Live Size Badge & Hide Action */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
              <Box
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  borderRadius: "4px",
                  px: 0.75,
                  py: 0.2,
                  fontSize: "0.675rem",
                  fontWeight: 700,
                  letterSpacing: "0.02em",
                }}
              >
                {activeSpan} cols × {activeHeight}px
              </Box>

              {registryInfo.allowHide !== false && (
                <Tooltip title={hidden ? "Show Widget" : "Hide Widget"}>
                  <IconButton
                    size="small"
                    onClick={() => onHide(id)}
                    sx={{ p: 0.25, color: "#FFFFFF", "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.2)" } }}
                  >
                    <EyeOff size={14} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        )}

        {/* Card Content Container with Full Flex Heights */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            minHeight: isCustomizing ? `calc(${activeHeight}px - 40px)` : `${activeHeight}px`,
            transition: isResizing ? "none" : "height 0.2s ease-in-out",
            "& > .MuiCard-root": {
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            },
          }}
        >
          {children}
        </Box>

        {/* Bottom-Right Interactive Resize Pill Handle */}
        {isCustomizing && (
          <Tooltip title="Drag to adjust width & height on 12-col grid">
            <Box
              onMouseDown={handleResizeMouseDown}
              sx={{
                position: "absolute",
                bottom: -10,
                right: -10,
                width: 28,
                height: 28,
                borderRadius: "50%",
                backgroundColor: "#2563EB",
                color: "#FFFFFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "nwse-resize",
                zIndex: 10,
                boxShadow: "0px 4px 10px rgba(37, 99, 235, 0.4)",
                border: "2px solid #FFFFFF",
                userSelect: "none",
                transition: "transform 0.15s",
                "&:hover": { transform: "scale(1.15)", backgroundColor: "#1D4ED8" },
                "&:active": { transform: "scale(1.2)" },
              }}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="3" cy="3" r="1.2" fill="white" />
                <circle cx="9" cy="3" r="1.2" fill="white" />
                <circle cx="3" cy="9" r="1.2" fill="white" />
                <circle cx="9" cy="9" r="1.2" fill="white" />
              </svg>
            </Box>
          </Tooltip>
        )}
      </Box>
    </Grid>
  );
};
